import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDashboardData } from "./useDashboardData";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Bell, UserPlus, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/contexts/UserContext";
import { sendChatNudge } from "@/components/chat/services/userProfileService";
import { generateTeamInvitation, createInvitationUrl } from "@/components/team/services/teamInviteService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, memo } from "react";

// Memoized team member component to prevent unnecessary rerenders
const TeamMemberItem = memo(({ 
  member, 
  onStartChat, 
  onSendNudge 
}: { 
  member: any; 
  onStartChat: (id: string, name: string) => void;
  onSendNudge: (id: string, name: string) => void;
}) => {
  return (
    <div className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-lg transition-colors">
      <div className="flex items-center space-x-3">
        <Avatar>
          <AvatarImage src={member.avatarUrl} />
          <AvatarFallback>
            {member.name.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{member.name}</p>
          <p className="text-sm text-muted-foreground">{member.role}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onStartChat(member.id, member.name)}
          className="flex items-center gap-1"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          <span>Chat</span>
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-1"
            >
              <Bell className="h-3.5 w-3.5" />
              <span>Nudge</span>
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Send a nudge to {member.name}
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will send a notification to let {member.name} know
                you'd like to connect.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onSendNudge(member.id, member.name)}
              >
                Send Nudge
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
});

TeamMemberItem.displayName = "TeamMemberItem";

export default function DashboardTeam() {
  const { teamMembers } = useDashboardData();
  const { user } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  
  // Check if user is an independent solo user (should see team transition)
  // Also include users with "individual" persona who have no team (legacy solo users)
  const isIndependentSolo = user?.persona === "independentSolo" || 
    (user?.persona === "individual" && !user?.teamId && !user?.organizationId);
  
  // Check if user is an organizational solo user (should see organization features)
  const isOrganizationalSolo = user?.persona === "organizationalSolo";
  
  // Check if user is a team lead with no team members (just transitioned)
  const isNewTeamLead = (user?.persona === "manager" || user?.persona === "organizationAdmin") && 
                        teamMembers.length === 0;

  // Function to handle starting a chat with a team member
  const handleStartChat = (userId: string, userName: string) => {
    if (!user) return;

    // Navigate to chat with this user
    navigate("/chat", { state: { connectUserId: userId } });

    // Show toast notification
    toast({
      title: "Chat started!",
      description: `You can now chat with ${userName}.`,
    });
  };

  // Function to handle sending a nudge to a team member
  const handleSendNudge = async (userId: string, userName: string) => {
    if (!user) return;

    try {
      // Send a nudge notification
      const success = await sendChatNudge(userId, user.id, user.name);

      if (!success) {
        throw new Error("Failed to send nudge");
      }

      // Show toast notification
      toast({
        title: "Nudge sent!",
        description: `Your nudge was sent to ${userName}.`,
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error sending nudge:", error);
      }
      toast({
        title: "Error",
        description: "Failed to send nudge. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Function to handle team member invitation
  const handleInviteTeamMember = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!inviteEmail.trim() || !emailRegex.test(inviteEmail.trim())) {
      toast({
        title: "Error",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    // Check if user has required data
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User session not found. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.teamId) {
      toast({
        title: "Error",
        description: "No team found. Please create or join a team first.",
        variant: "destructive",
      });
      return;
    }

    setIsInviting(true);
    try {
      // Generate invitation code using the service
      const inviteCode = await generateTeamInvitation(user.id, user.teamId, 7);
      
      if (!inviteCode) {
        throw new Error("Failed to generate invitation code");
      }

      // Create invitation URL
      const inviteUrl = createInvitationUrl(inviteCode);
      
      // Copy invitation link to clipboard
      await navigator.clipboard.writeText(inviteUrl);
      
      toast({
        title: "Invitation created!",
        description: "Invitation link copied to clipboard. Share it with your team member.",
      });
      
      setInviteEmail("");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error creating invitation:", error);
      }
      toast({
        title: "Error",
        description: "Failed to create invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Show team transition for independent solo users
  if (isIndependentSolo) {
    return (
      <Card className="col-span-12 lg:col-span-4">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Collaborate with Step</CardTitle>
        </CardHeader>
        <CardContent className="px-4">
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Working Solo Today</h3>
              <p className="text-muted-foreground mb-6">
                You're currently using Step as an individual contributor. 
                When you're ready to collaborate, Step makes it easy to work with teams.
              </p>
              
              <div className="text-left space-y-3 bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-sm">With Step Teams, you can:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Share progress and insights with teammates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Get AI-powered team collaboration suggestions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Track team morale and energy levels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Schedule check-ins and sync meetings</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground mb-3">
                  Ready to collaborate?
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/team-transition")}
                >
                  Start Team Transition
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show organization collaboration for organizational solo users
  if (isOrganizationalSolo) {
    return (
      <Card className="col-span-12 lg:col-span-4">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Organization Network</CardTitle>
        </CardHeader>
        <CardContent className="px-4">
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m14 0v2a1 1 0 01-1 1h-1a1 1 0 01-1-1v-2m0 0V9a1 1 0 011-1h1a1 1 0 011 1v12m-7 0a1 1 0 01-1-1v-1a1 1 0 011-1h1a1 1 0 011 1v1a1 1 0 01-1 1h-1z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Connected to Your Organization</h3>
              <p className="text-muted-foreground mb-6">
                You're part of an organization but not assigned to a specific team. 
                You can collaborate with other organization members.
              </p>
              
              <div className="text-left space-y-3 bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-sm">Organization Features:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Connect with organization members</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Share skills and expertise</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Access organization insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Collaborate across departments</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground mb-3">
                  Explore your organization network
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/team")}
                >
                  View Organization
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show team building interface for new team leads
  if (isNewTeamLead) {
    return (
      <Card className="col-span-12 lg:col-span-4">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Build Your Team</CardTitle>
        </CardHeader>
        <CardContent className="px-4">
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <UserPlus className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Ready to Build Your Team</h3>
              <p className="text-muted-foreground mb-6">
                You've transitioned to team mode! Start by inviting your team members.
              </p>
            </div>
            
            <div className="space-y-3">
              <Label htmlFor="inviteEmail">Invite Team Member</Label>
              <div className="flex gap-2">
                <Input
                  id="inviteEmail"
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInviteTeamMember()}
                />
                <Button 
                  onClick={handleInviteTeamMember}
                  disabled={isInviting || !inviteEmail.trim()}
                >
                  {isInviting ? "..." : <Mail className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-medium text-sm mb-2">Next Steps:</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Invite team members via email</li>
                <li>• Team members will receive Step accounts</li>
                <li>• Start collaborating with shared insights</li>
                <li>• Track team progress and morale</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-12 lg:col-span-4">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Your Team</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Show invite dialog or navigate to invite page
            navigate("/team");
          }}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite
        </Button>
      </CardHeader>
      <CardContent className="px-2">
        <div className="space-y-4">
          {teamMembers.map((member) => (
            <TeamMemberItem
              key={member.id}
              member={member}
              onStartChat={handleStartChat}
              onSendNudge={handleSendNudge}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
